import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Menu, X, Globe } from "lucide-react";
import { BrushButton } from "@/components/ui/brush-button";
const logo = "/images/logo.webp";
const logoWhite = "/images/logo-white.webp";
const logoDark = "/images/logo-dark.png";

interface NavbarProps {
  forceHide?: boolean;
  quizMode?: boolean;
  chatMode?: boolean;
}

const Navbar = ({ forceHide = false, quizMode = false, chatMode = false }: NavbarProps) => {
  const { language, setLanguage, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [navOpacity, setNavOpacity] = useState(1);
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const isJobsBoard = location.pathname === "/jobs-board";
  const isWhiteNav = isHomePage || isJobsBoard;
  const isQuizOrChat = location.pathname === "/quiz" || location.pathname === "/chat";

  useEffect(() => {
    // Chat mode: fade out navbar completely on scroll
    if (chatMode) {
      const handleScroll = () => {
        const scrollY = window.scrollY;
        const fadeStart = 10;
        const fadeEnd = 80;

        const opacity =
          scrollY <= fadeStart
            ? 1
            : scrollY >= fadeEnd
            ? 0
            : 1 - (scrollY - fadeStart) / (fadeEnd - fadeStart);

        setNavOpacity(opacity);
      };

      window.addEventListener("scroll", handleScroll, { passive: true });
      handleScroll();
      return () => window.removeEventListener("scroll", handleScroll);
    }

    // Quiz mode: fade out on scroll
    if (quizMode) {
      const handleScroll = () => {
        const fadeStart = 50;
        const fadeEnd = 150;
        const scrollY = window.scrollY;

        const opacity =
          scrollY <= fadeStart
            ? 1
            : scrollY >= fadeEnd
            ? 0
            : 1 - (scrollY - fadeStart) / (fadeEnd - fadeStart);

        setNavOpacity(opacity);
      };

      window.addEventListener("scroll", handleScroll, { passive: true });
      handleScroll(); // Check initial scroll position
      return () => window.removeEventListener("scroll", handleScroll);
    }

    // Home page & Jobs Board: fade on scroll
    if (isWhiteNav) {
      const handleScroll = () => {
        const fadeStart = isJobsBoard ? 10 : 100;
        const fadeEnd = isJobsBoard ? 80 : 350;
        const scrollY = window.scrollY;

        const opacity =
          scrollY <= fadeStart
            ? 1
            : scrollY >= fadeEnd
            ? 0
            : 1 - (scrollY - fadeStart) / (fadeEnd - fadeStart);

        setNavOpacity(opacity);
      };

      window.addEventListener("scroll", handleScroll, { passive: true });
      handleScroll();
      return () => window.removeEventListener("scroll", handleScroll);
    }

    // Other pages: always visible
    setNavOpacity(1);
  }, [isHomePage, isWhiteNav, isJobsBoard, quizMode, chatMode]);

  const isActive = (path: string) => location.pathname === path;
  const navLinks = [
    {
      path: "/",
      label: t("nav.home"),
    },
    {
      path: "/quiz",
      label: t("nav.quiz"),
    },
    {
      path: "/chat",
      label: t("nav.chat"),
    },
    {
      path: "/jobs-board",
      label: t("nav.jobsBoard"),
    },
  ];

  const effectiveOpacity = forceHide ? 0 : navOpacity;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 bg-transparent transition-opacity duration-300 ${isQuizOrChat ? "pt-2" : ""}`}
      style={{
        opacity: effectiveOpacity,
        pointerEvents: effectiveOpacity < 0.1 ? "none" : "auto",
      }}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className={`flex items-center gap-2 group -ml-4 sm:-ml-6 lg:-ml-8 ${isQuizOrChat ? "mt-2" : "mt-4"}`}>
            <img 
              src={isWhiteNav ? logoWhite : isQuizOrChat ? logoDark : logo} 
              alt="Your Office Outdoors" 
              className={`border-0 object-contain drop-shadow-lg ${isQuizOrChat ? "h-16 md:h-24" : "h-28 md:h-40"}`}
              fetchPriority="high"
              width={240}
              height={160}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-md">
            {navLinks.map((link, index) => (
              <div key={link.path} className="flex items-center gap-md">
              <Link
                  to={link.path}
                  className={`text-body font-bold transition-colors duration-fast ${
                    isWhiteNav 
                      ? "text-white hover:text-white/80 drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]" 
                      : "text-forest-900 hover:text-forest-600 drop-shadow-[0_1px_2px_rgba(255,255,255,0.8)]"
                  } ${isActive(link.path) ? (isWhiteNav ? "text-white/90" : "text-forest-700") : ""}`}
                >
                  {link.label}
                </Link>
                {index < navLinks.length - 1 && <div className={`h-6 w-px ${isWhiteNav ? "bg-white/50" : "bg-forest-800"}`} />}
              </div>
            ))}

            {/* Language Toggle */}
            <BrushButton
              onClick={() => setLanguage(language === "en" ? "es" : "en")}
              variant="primary"
              className="text-sm py-2 px-6"
            >
              <span className="flex items-center gap-2">
                <Globe className="h-4 w-4" />
                {language === "en" ? "ES" : "EN"}
              </span>
            </BrushButton>
          </div>

          {/* Mobile: Language Toggle + Menu Button */}
          <div className="md:hidden flex items-center gap-1">
            <button
              onClick={() => setLanguage(language === "en" ? "es" : "en")}
              className={`flex items-center gap-1 p-2 transition-colors font-bold text-sm ${
                isWhiteNav 
                  ? "text-white hover:text-white/80 drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]" 
                  : "text-forest-900 hover:text-forest-600 drop-shadow-[0_1px_2px_rgba(255,255,255,0.8)]"
              }`}
              aria-label="Toggle language"
            >
              <Globe className="h-4 w-4" />
              {language === "en" ? "ES" : "EN"}
            </button>
            <button
              className={`p-2 transition-colors ${
                isWhiteNav 
                  ? "text-white hover:text-white/80 drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]" 
                  : "text-forest-900 hover:text-forest-600 drop-shadow-[0_1px_2px_rgba(255,255,255,0.8)]"
              }`}
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-lg bg-forest-900/90 backdrop-blur-md rounded-lg mt-2 animate-slide-up">
            <div className="flex flex-col gap-md px-md">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`text-body font-medium py-sm transition-colors hover:text-sunset-300 text-white ${isActive(link.path) ? "text-sunset-300" : ""}`}
                >
                  {link.label}
                </Link>
              ))}
              <BrushButton asChild variant="primary" className="mt-sm">
                <Link to="/quiz" onClick={() => setIsOpen(false)}>
                  {t("nav.quiz")}
                </Link>
              </BrushButton>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
export default Navbar;
